import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { FaEdit, FaTrash } from "react-icons/fa";
import toast from "react-hot-toast";
import EditProjectForm from "./EditProjectForm";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_BASE_URL } from "../../../../config";

const ProjectTable = ({
  search,
  projects,
  onRefresh,
  adminPermissions,
  isSuperAdmin,
  page,
  totalPages,
  onPageChange,
}) => {
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProjectData, setEditProjectData] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [localPage, setLocalPage] = useState(1);
  const allProjectsCache = useRef(null);

  // ✅ Filters State
  const [filters, setFilters] = useState({
    projectName: "",
    area: "",
    propertyType: "",
    buildingFloors: "",
    flatType: "",
    flatFacing: "",
    superBuiltUp: "",
    ratePerSqft: "",
    flatPrice: "",
  });

  // Sync paginated projects into filteredProjects (when no search active)
  useEffect(() => {
    if (!search && !isFiltered) setFilteredProjects(projects);
  }, [projects, search, isFiltered]);

  // Search across ALL records
  useEffect(() => {
    if (!search) {
      allProjectsCache.current = null;
      if (!isFiltered) setFilteredProjects(projects);
      return;
    }

    const lowerSearch = search.toLowerCase();
    const doFilter = (allData) => {
      setFilteredProjects(
        allData.filter((p) =>
          Object.values(p).some((val) =>
            val?.toString().toLowerCase().includes(lowerSearch)
          )
        )
      );
    };

    if (allProjectsCache.current) {
      doFilter(allProjectsCache.current);
      return;
    }

    const token = localStorage.getItem("token");
    axios
      .get(`${API_BASE_URL}/api/project/get-all-projects?page=1&limit=99999`, { headers: { token } })
      .then((res) => {
        if (res.data.success) {
          allProjectsCache.current = res.data.result;
          doFilter(allProjectsCache.current);
        }
      })
      .catch(() => {});
  }, [search, projects]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setLocalPage(1); }, [filteredProjects]);

  // ✅ Filter Projects API Call
  const applyFilters = async () => {
    const token = localStorage.getItem("token");
    const loadingToast = toast.loading("Applying filters...");

    try {
      const query = {};
      Object.keys(filters).forEach((key) => {
        if (filters[key]) query[key] = filters[key];
      });

      const queryString = new URLSearchParams(query).toString();

      const response = await axios.post(
        `${API_BASE_URL}/api/project/filter-project?${queryString}`,
        {},
        { headers: { token } }
      );

      toast.dismiss(loadingToast);

      if (response.data.success) {
        setFilteredProjects(response.data.result);
        setIsFiltered(true);
        toast.success(`Filters applied! ${response.data.count} projects found`);
      } else {
        toast.error("No projects found for selected filters.");
      }
    } catch (error) {
      // console.error("Filter error:", error);
      toast.dismiss(loadingToast);
      toast.error("Something went wrong while filtering.");
    }
  };

  const resetFilters = () => {
    setFilters({
      projectName: "",
      area: "",
      propertyType: "",
      buildingFloors: "",
      handOver: "",
      flatType: "",
      flatFacing: "",
      superBuiltUp: "",
      ratePerSqft: "",
      flatPrice: "",
      visitedStatus: "",
    });
    setIsFiltered(false);
    setFilteredProjects(projects);
  };

  const handleRefresh = () => {
    allProjectsCache.current = null;
    setIsFiltered(false);
    onRefresh();
  };

  const hasPermission = (action) => {
    return (
      isSuperAdmin ||
      (adminPermissions?.project && adminPermissions.project[action])
    );
  };

  const confirmDelete = (id) => {
    if (!hasPermission("delete")) {
      toast.error("You don't have permission to delete projects");
      return;
    }
    setSelectedProjectId(id);
    setShowDeleteModal(true);
  };

  const openEditForm = (project) => {
    if (!hasPermission("edit")) {
      toast.error("You don't have permission to edit projects");
      return;
    }
    setEditProjectData(project);
    setShowEditModal(true);
  };



  const performDelete = async () => {
    const token = localStorage.getItem("token");
    const deletingToast = toast.loading("Deleting project...");

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/project/delete-project/${selectedProjectId}`,
        {},
        { headers: { token } }
      );

      toast.dismiss(deletingToast);

      if (res.data.success) {
        toast.success("Project deleted successfully!");
        handleRefresh();
      } else {
        toast.error(res.data.message || "Deletion failed.");
      }
    } catch (err) {
      // console.error(err);
      toast.dismiss(deletingToast);
      toast.error("Something went wrong while deleting.");
    } finally {
      setShowDeleteModal(false);
      setSelectedProjectId(null);
    }
  };



  const tableHeaders = [
    "projectName",
    "area",
    "projectAddress",
    "electricitySection",
    "propertyType",
    "frontRoadSize",
    "waterSupply",
    "propertyStatus",
    "handOver",
    "noOfTower",
    "projectType",
    "buildingFloors",
    "propertyOnFloorNumber",
    "projectContactName",
    "projectContactNumber",
    "flatType",
    "flatFacing",
    "superBuiltUp",
    "deductionPercentage",
    "carpetArea",
    "ratePerSqft",
    "flatPrice",
    "noOfFlatAvailable",
    "approvedBank",
    "parkingType",
    "parkingRate",
    "amenitiesCharge",
    "maintenanceCharge",
    "anyOtherCharges",
    "amenities",
    "mainRoadName",
    "distanceFromMainRoad",
    "nearestStationName",
    "distanceFromRailwayStation",
    "nearestBusStopName",
    "distanceFromBusStop",
    "distanceFromLocalMarket",
    "remarks",
    "photosOrVideos",
    "visitedStatus",
    "status",
  ];

  const formatHeader = (field) => {
    return field
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <>
      {/* 🔹 Filter Section */}
      <div className="mb-4 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Project Name"
          value={filters.projectName}
          onChange={(e) =>
            setFilters({ ...filters, projectName: e.target.value })
          }
          className="rounded border p-2"
        />
        <input
          type="text"
          placeholder="Area"
          value={filters.area}
          onChange={(e) => setFilters({ ...filters, area: e.target.value })}
          className="rounded border p-2"
        />
        <select
          value={filters.propertyType}
          onChange={(e) =>
            setFilters({ ...filters, propertyType: e.target.value })
          }
          className="rounded border p-2"
        >
          <option value="">All Property Types</option>
          <option value="Residential">Residential</option>
          <option value="Commercial">Commercial</option>
          <option value="Rent">Rent</option>
        </select>
        <input
          type="text"
          placeholder="Building Floors"
          value={filters.buildingFloors}
          onChange={(e) =>
            setFilters({ ...filters, buildingFloors: e.target.value })
          }
          className="rounded border p-2"
        />

        {/* Month Filter */}
        <input
          type="month"
          value={filters.handOver}
          onChange={(e) => setFilters({ ...filters, handOver: e.target.value })}
          className="w-40 rounded border p-2"
        />


        <DatePicker
          selected={filters.handOver ? new Date(parseInt(filters.handOver), 0, 1) : null}
          onChange={(date) => {
            if (date) {
              const year = date.getFullYear();
              setFilters({ ...filters, handOver: year });
            } else {
              setFilters({ ...filters, handOver: null });
            }
          }}
          showYearPicker
          dateFormat="yyyy"
          placeholderText="Select Year"
          className="w-32 rounded border p-2"
          popperClassName="z-[9999]"
        />

        {/* <DatePicker
          selected={filters.handOver ? new Date(filters.handOver, 0) : null}
          onChange={(date) => {
            const year = date.getFullYear();
            setFilters({ ...filters, handOver: year });
          }}
          showYearPicker
          dateFormat="yyyy"
          placeholderText="Select Year"
          className="w-32 rounded border p-2 "
          popperClassName="z-[9999]"
        /> */}

        <select
          value={filters.flatType}
          onChange={(e) => setFilters({ ...filters, flatType: e.target.value })}
          className="rounded border p-2"
        >
          <option value="">All Flat Types</option>
          <option value="1BHK">1BHK</option>
          <option value="2BHK">2BHK</option>
          <option value="3BHK">3BHK</option>
          <option value="4BHK">4BHK</option>
        </select>

        <select
          value={filters.flatFacing}
          onChange={(e) =>
            setFilters({ ...filters, flatFacing: e.target.value })
          }
          className="rounded border p-2"
        >
          <option value="">Flat Facing</option>
          <option value="North-East">North-East</option>
          <option value="South-East">South-East</option>
          <option value="West-South">West-South</option>
          <option value="West-North">West-North</option>
        </select>
        <input
          type="number"
          placeholder="Super Built Up (sqft)"
          value={filters.superBuiltUp}
          onChange={(e) =>
            setFilters({ ...filters, superBuiltUp: e.target.value })
          }
          className="w-40 rounded border p-2"
        />
        <input
          type="number"
          placeholder="Rate Per Sqft"
          value={filters.ratePerSqft}
          onChange={(e) =>
            setFilters({ ...filters, ratePerSqft: e.target.value })
          }
          className="w-40 rounded border p-2"
        />
        <input
          type="number"
          placeholder="Flat Price"
          value={filters.flatPrice}
          onChange={(e) =>
            setFilters({ ...filters, flatPrice: e.target.value })
          }
          className="w-40 rounded border p-2"
        />
        <select
          value={filters.visitedStatus}
          onChange={(e) =>
            setFilters({ ...filters, visitedStatus: e.target.value })
          }
          className="rounded border p-2"
        >
          <option value="">Visited Status</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>

        <button
          onClick={applyFilters}
          className="rounded bg-red-500 px-4 py-2 text-white"
        >
          Apply Filters
        </button>
        <button
          onClick={resetFilters}
          className="rounded bg-gray-400 px-4 py-2 text-white"
        >
          Reset
        </button>
      </div>

      <div className="max-h-[500px] overflow-x-auto overflow-y-auto rounded-lg bg-white shadow dark:bg-navy-700">
        <table className="min-w-full text-left text-sm text-gray-700 dark:text-gray-100">
          <thead className="text-black sticky top-0 z-20 border-b border-gray-200 bg-gray-100 text-xs font-bold dark:bg-gray-700 dark:text-white">
            <tr>
              <th className="sticky left-0 top-0 z-30 whitespace-nowrap border bg-gray-100 px-3 py-2 dark:bg-gray-700">
                SL No
              </th>
              {tableHeaders.map((field, index) => (
                <th key={index} className="whitespace-nowrap border px-3 py-2">
                  {formatHeader(field)}
                </th>
              ))}

              <th className="whitespace-nowrap border px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const isLocalMode = isFiltered || !!search;
              const localTotalPages = Math.max(1, Math.ceil(filteredProjects.length / 50));
              const displayedProjects = isLocalMode
                ? filteredProjects.slice((localPage - 1) * 50, localPage * 50)
                : filteredProjects;
              return displayedProjects.length > 0 ? (
              displayedProjects.map((proj, index) => (
                <tr
                  key={proj._id}
                  className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                >
                  <td className="sticky left-0 z-10 whitespace-nowrap border bg-white px-3 py-2 dark:bg-navy-700">
                    {isLocalMode ? (localPage - 1) * 50 + index + 1 : (page - 1) * 50 + index + 1}
                  </td>
                  {tableHeaders.map((key, idx) => (
                    <td
                      key={idx}
                      className="whitespace-nowrap border px-2 py-2"
                    >
                      {key === "status" ? (
                        <span
                          className={`font-semibold ${proj[key]?.toLowerCase() === "active"
                            ? "text-green-600"
                            : proj[key]?.toLowerCase() === "delete"
                              ? "text-red-600"
                              : "text-gray-600"
                            }`}
                        >
                          {proj[key]}
                        </span>
                      ) : key === "handOver" && proj[key] ? (
                        new Date(proj[key]).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                        })
                      ) : (
                        proj[key]
                      )}
                    </td>
                  ))}
                  {/* <td className="flex gap-2 whitespace-nowrap px-2 py-2">
                    <button
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => openEditForm(proj)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="text-red-600 hover:text-red-800"
                      onClick={() => confirmDelete(proj._id)}
                    >
                      <FaTrash />
                    </button>
                  </td> */}

                  <td className="flex gap-2 whitespace-nowrap px-2 py-2">
                    {/* Edit Button */}
                    <button
                      className={`${hasPermission("edit")
                        ? "text-blue-600 hover:text-blue-800"
                        : "cursor-not-allowed text-gray-400 opacity-60"
                        }`}
                      onClick={() =>
                        hasPermission("edit") && openEditForm(proj)
                      }
                      disabled={!hasPermission("edit")}
                      title={
                        !hasPermission("edit")
                          ? "No permission to edit"
                          : "Edit Project"
                      }
                    >
                      <FaEdit />
                    </button>

                    {/* Delete Button */}
                    <button
                      className={`${hasPermission("delete")
                        ? "text-red-600 hover:text-red-800"
                        : "cursor-not-allowed text-gray-400 opacity-60"
                        }`}
                      onClick={() =>
                        hasPermission("delete") && confirmDelete(proj._id)
                      }
                      disabled={!hasPermission("delete")}
                      title={
                        !hasPermission("delete")
                          ? "No permission to delete"
                          : "Delete Project"
                      }
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="100%" className="py-4 text-center text-gray-500">
                  No matching projects found.
                </td>
              </tr>
            );})()}
          </tbody>
        </table>
      </div>

      {/* Pagination helper */}
      {(() => {
        const isLocalMode = isFiltered || !!search;
        const activePage = isLocalMode ? localPage : page;
        const activeTotal = isLocalMode ? Math.max(1, Math.ceil(filteredProjects.length / 50)) : totalPages;
        const handleChange = isLocalMode ? setLocalPage : onPageChange;
        if (activeTotal < 1) return null;
        return (
          <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
            <button onClick={() => handleChange(1)} disabled={activePage === 1}
              className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700">«</button>
            <button onClick={() => handleChange(activePage - 1)} disabled={activePage === 1}
              className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700">Previous</button>

            {Array.from({ length: activeTotal }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === activeTotal || Math.abs(p - activePage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "..." ? (
                  <span key={`dots-${idx}`} className="px-2 text-gray-500 dark:text-gray-400">...</span>
                ) : (
                  <button key={item} onClick={() => handleChange(item)}
                    className={`rounded border px-3 py-1 text-sm ${activePage === item ? "bg-indigo-600 text-white border-indigo-600" : "text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700"}`}
                  >{item}</button>
                )
              )}

            <button onClick={() => handleChange(activePage + 1)} disabled={activePage === activeTotal}
              className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700">Next</button>
            <button onClick={() => handleChange(activeTotal)} disabled={activePage === activeTotal}
              className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700">»</button>
            <span className="text-sm text-gray-600 dark:text-white ml-2">
              Page {activePage} of {activeTotal}
              {isLocalMode && ` (${filteredProjects.length} results)`}
            </span>
          </div>
        );
      })()}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="w-[300px] rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold">
              Are you sure you want to Delete?
            </h2>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="rounded-md border px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={performDelete}
                className="rounded-md bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="bg-black/30 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="max-h-[90vh] w-[90%] max-w-6xl overflow-y-auto rounded-lg bg-white p-6 shadow-lg">
            <EditProjectForm
              initialData={editProjectData}
              onClose={() => {
                setShowEditModal(false);
                setEditProjectData(null);
              }}
              onSuccess={handleRefresh}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectTable;
